import { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  Button,
  HomeAddressIcon,
  Layout,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { FloatingField } from "../../components/FloatingField";
import { PAYOUT_CLAIM_ROUTES, TITLE_COLOR } from "../../constants";
import { COUNTRIES } from "../../constants/countries";
import { useStore } from "../../store/globalState";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";
import { useUsBillingZip } from "../../utils/useUsBillingZip";

/**
 * Static OFAC disambiguation form for gallery inspection.
 * Uses the same mock-card input shell as Add Card / Billing Address.
 */
export const OfacDisambiguationPage = memo(() => {
  const { t } = useTranslation("payout-claim");
  const { theme } = useStore();
  const navigate = useNavigate();
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy, dropzoneColors } = colorConfig;
  const fieldColors = {
    background: colorConfig.background,
    title: colorConfig.title,
    bodyCopy,
    inactiveColor: colorConfig.subtitile,
    errorColor: colorConfig.errorColor,
  };

  const [dob, setDob] = useState("1990-01-15");
  const [street, setStreet] = useState("350 Fifth Avenue");
  const [street2, setStreet2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("10118");
  const [country, setCountry] = useState("US");
  const [submitting, setSubmitting] = useState(false);
  const isUS = country === "US";
  const { zipLocation, zipUnknown } = useUsBillingZip(isUS, zip);

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    setSubmitting(true);
    window.setTimeout(() => {
      setSubmitting(false);
      navigate(PAYOUT_CLAIM_ROUTES.SENDING_MONEY, { replace: true });
    }, 600);
  };

  return (
    <AuraLayout colorConfig={colorConfig} className="scrollable need-help-header">
      <Layout.Header>
        <Navbar
          title={t("common.needHelp")}
          align="right"
          onBackClick={() => navigate(PAYOUT_CLAIM_ROUTES.TRANSACTIONS)}
          onIconClick={() => navigate(PAYOUT_CLAIM_ROUTES.SUPPORT)}
          icon={<SupportIcon size={35} colors={dropzoneColors} />}
        />
      </Layout.Header>
      <Layout.Content className="transfer-section">
        <ScrollFadeIndicator color={bodyCopy} />
        <Title
          title={t("ofacDisambiguation.title")}
          align="left"
          color={TITLE_COLOR}
        />
        <BodyCopy
          textColor={bodyCopy}
          className="ofac-disambiguation-helper"
        >
          {t("ofacDisambiguation.helper")}
        </BodyCopy>
        <form
          id="ofac-disambiguation-form"
          className="mock-card-form"
          autoComplete="on"
          onSubmit={handleSubmit}
        >
          <section className="mock-card-fields">
            <div className="mock-card-field">
              <FloatingField
                label={t("ofacDisambiguation.dateOfBirth")}
                value={dob || " "}
                required
                colors={fieldColors}
              >
                <input
                  id="ofac-dob"
                  style={{ color: bodyCopy }}
                  type="date"
                  name="dateOfBirth"
                  autoComplete="bday"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                />
              </FloatingField>
            </div>

            <div className="mock-card-field">
              <FloatingField
                label={t("ofacDisambiguation.street")}
                value={street}
                required
                colors={fieldColors}
                icon={<HomeAddressIcon colors={dropzoneColors} size={18} />}
              >
                <input
                  style={{ color: bodyCopy }}
                  name="line1"
                  autoComplete="address-line1"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                />
              </FloatingField>
            </div>

            <div className="mock-card-field">
              <FloatingField
                label={t("ofacDisambiguation.street2")}
                value={street2}
                colors={fieldColors}
              >
                <input
                  style={{ color: bodyCopy }}
                  name="line2"
                  autoComplete="address-line2"
                  value={street2}
                  onChange={(e) => setStreet2(e.target.value)}
                />
              </FloatingField>
            </div>

            {!isUS && (
              <>
                <div className="mock-card-field">
                  <FloatingField
                    label={t("ofacDisambiguation.city")}
                    value={city}
                    required
                    colors={fieldColors}
                  >
                    <input
                      style={{ color: bodyCopy }}
                      name="city"
                      autoComplete="address-level2"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    />
                  </FloatingField>
                </div>

                <div className="mock-card-field">
                  <FloatingField
                    label={t("ofacDisambiguation.state")}
                    value={state}
                    required
                    colors={fieldColors}
                  >
                    <input
                      style={{ color: bodyCopy }}
                      name="state"
                      autoComplete="address-level1"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                    />
                  </FloatingField>
                </div>
              </>
            )}

            <div className="mock-card-field">
              <FloatingField
                label={t("ofacDisambiguation.zip")}
                value={zip}
                required
                colors={fieldColors}
              >
                <input
                  style={{ color: bodyCopy }}
                  name="zip"
                  autoComplete="postal-code"
                  inputMode={isUS ? "numeric" : "text"}
                  value={zip}
                  onChange={(e) =>
                    setZip(isUS ? e.target.value.replace(/[^\d-]/g, "") : e.target.value)
                  }
                />
              </FloatingField>
              {zipUnknown && (
                <BodyCopy textColor={colorConfig.errorColor}>
                  {t("ofacDisambiguation.zipNotFound")}
                </BodyCopy>
              )}
            </div>

            <div className="mock-card-field">
              <FloatingField
                label={t("ofacDisambiguation.country")}
                value={country}
                required
                colors={fieldColors}
              >
                <select
                  style={{ color: bodyCopy }}
                  name="country"
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    setCity("");
                    setState("");
                  }}
                >
                  {COUNTRIES.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </FloatingField>
            </div>
          </section>
        </form>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            disabled={submitting || (isUS && !zipLocation)}
            onClick={() => handleSubmit()}
          >
            {submitting
              ? t("ofacDisambiguation.submitting")
              : t("ofacDisambiguation.submit")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
